import { useRef, useState, useCallback, Fragment } from 'react'
import { useId } from 'react'
import { motion, transform, useScroll, useTransform } from 'framer-motion'

import Link from '@/components/link-component'
import SearchNavIcon from '@/components/search/nav-icon'
import NavContainer from '@/components/nav-container'
import HeaderDropdownMenu from '@/components/header-dropdown-menu'
import HeaderRichMenu, { HeaderRichMenuContent } from '@/components/header-rich-menu'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import StaggerList, { StaggerListItem } from '@/components/ui/stagger-list'
import HeaderBanner from '@/components/header-banner'
import Disclosure from '@/components/ui/disclosure'
import DynamicAction from '@/components/ui/dynamic-action'
import { Experience } from '@ninetailed/experience.js-react'
import { mapNtExperiences } from '@/utils/ninetailed'
import routes from '@/constants/routes'

import Hamburger from '@/icons/bwi/hamburger.svg'
import Close from '@/icons/bwi/close.svg'
import LogoHorizontal from '@/icons/logo-horizontal.svg'
import ArrowDown from '@/icons/bwi/angle-down.svg'

import colors from '@/constants/colors'
import { AnyLinkType, flattenLinkGroups } from '@/utils/header'
import { cx } from '@/utils/strings.ts'
import { BREAKPOINTS, useBreakpoint } from '@/utils/responsive'
import { isTypeComponentCtaLink, isTypeHeaderDropdownMenu, isTypeHeaderRichMenu, TypeHeader } from '@/types/ctf'

function MobileDisclosureArrow(isOpen: boolean) {
  return (
    <span
      className={cx(`w-4 h-4 [&_svg]:text-black [&_svg]:dark:text-white transition-transform`, isOpen && `rotate-180`)}
    >
      <ArrowDown />
    </span>
  )
}

type HeaderComponentProps = TypeHeader<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  headerBanners: any
  newsletterForm: any
}

export function HeaderComponent({
  backgroundColor = 'White',
  ctaNavItems,
  mainNavItems,
  showSearch,
  headerBanners,
  newsletterForm,
  isStatic = false,
}: HeaderComponentProps) {
  // Primary blue is retired, so we use white as the default background color
  // TODO: Migrate all instances of primary blue to white in contentful after new design launch
  if (backgroundColor === colors.primaryBlue) {
    backgroundColor = 'White'
  }

  const mainNavLinks = flattenLinkGroups(mainNavItems)
  const ctaNavLinks = flattenLinkGroups(ctaNavItems)

  const updateMobileDownloadLink = (ctaLink: any) => {
    if (ctaLink?.link?.url?.endsWith('/download/')) {
      return {
        ...ctaLink,
        link: {
          ...ctaLink,
          url: `${ctaLink.link.url}#downloads-mobile`,
        },
      }
    }
    return ctaLink
  }
  function transformCustomMobileLinks<T extends AnyLinkType>(link: T): T {
    if (!link) return link
    if ('sys' in link) {
      if (isTypeComponentCtaLink(link)) {
        return updateMobileDownloadLink(link)
      }
      if (isTypeHeaderDropdownMenu(link)) {
        return {
          ...link,
          links: link.fields.links?.map((nestedLink) => updateMobileDownloadLink(nestedLink)),
        }
      }
      if (isTypeHeaderRichMenu(link)) {
        return {
          ...link,
          cards: link.fields.cards?.map((card) => ({
            ...card,
            links: card?.fields.links?.map((nestedLink) => ({
              ...nestedLink,
              link: updateMobileDownloadLink(nestedLink?.fields.link),
            })),
          })),
          links: link.fields.links,
        }
      }
    }

    return link
  }

  const AreMainNavItemsEmpty = (mainNavItems?.length || 0) === 0
  const mobileMainNavLinks = flattenLinkGroups(!AreMainNavItemsEmpty ? mainNavItems : ctaNavItems).map(
    transformCustomMobileLinks
  )

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const closeDrawer = () => setIsDrawerOpen(false)
  const headerRef = useRef<HTMLElement>(null)

  let ctaBtnSolidCount = 0

  const { scrollY } = useScroll()

  const bgColorClasses = cx(
    backgroundColor === colors.white && `bg-white text-primaryBlue`,
    backgroundColor === colors.primaryBlue && `bg-primaryBlue text-white [*>a]:text-white`,
    'dark:bg-secondary-900'
  )
  const borderClasses = cx(
    backgroundColor === colors.white && `border-b border-secondary-100`,
    backgroundColor === colors.primaryBlue && `border-b border-primary-700`,
    'dark:border-secondary-700'
  )

  // Trigger rerender on breakpoint change to update scroll-based transforms
  // This to workaround a framer-motion issue where transforms don't recalculate on resize
  useBreakpoint()

  const transition = {
    // Prevent animation on page load
    // Workaround the header jumping on first scroll due to framer-motion applying transforms immediately
    duration: 0,
  }

  return (
    <>
      <HeaderBanner banners={headerBanners} />
      <motion.header
        className={cx(
          'w-full z-50',
          !isStatic && `sticky top-0 left-0 xl:top-6 xl:px-4 mb-0 xl:mb-10 transition-colors`,
          isStatic && 'relative',
          bgColorClasses,
          borderClasses,
          isDrawerOpen && `z-10000`
        )}
        ref={headerRef}
        transition={transition}
        style={{
          y: useTransform(scrollY, (latest) => {
            const isSSR = typeof window === 'undefined'
            if (isStatic || isSSR) return 0
            const screenWidth = isSSR ? 0 : window.innerWidth
            if (screenWidth >= BREAKPOINTS.xl) {
              return transform(latest, [0, 400], [0, -24])
            }
            return 0
          }),
          backgroundColor: useTransform(scrollY, (latest) => {
            if (isStatic) {
              return ''
            }
            if (latest < 400) {
              return 'transparent'
            }
            return ''
          }),
          borderColor: useTransform(scrollY, (latest) => {
            const isSSR = typeof window === 'undefined'
            if (isStatic || isSSR) {
              return ''
            }
            const screenWidth = window.innerWidth
            if (screenWidth < BREAKPOINTS.lg) {
              return 'transparent'
            }
            if (latest < 400) {
              if (screenWidth >= BREAKPOINTS.lg && screenWidth < BREAKPOINTS.xl) {
                return ''
              }
              return 'transparent'
            }
            return ''
          }),
        }}
      >
        {/* Mobile Nav */}
        <nav
          data-testid="bw-mobile-header"
          className={cx(
            'relative max-w-7xl mx-auto px-5 flex justify-between items-center py-4 lg:hidden',
            !isStatic && bgColorClasses,
            !isStatic && borderClasses
          )}
        >
          <div className="w-32">
            <Link to={routes.index} title="Bitwarden homepage">
              <LogoHorizontal
                className={cx(
                  'w-full',
                  backgroundColor === colors.primaryBlue && `text-white`,
                  backgroundColor === colors.white && `text-primaryBlue dark:text-white`
                )}
              />
            </Link>
          </div>
          <div className="flex gap-3 items-center">
            {showSearch && <SearchNavIcon />}
            {mobileMainNavLinks.length + ctaNavLinks.length > 0 && (
              <button
                type="button"
                className={cx(
                  'w-7 h-7 flex cursor-pointer transition-colors duration-200 hover:text-secondary-200 dark:text-white',
                  isDrawerOpen && `pointer-events-none`
                )}
                data-name="bwi-hamburger"
                onClick={() => {
                  setIsDrawerOpen(!isDrawerOpen)
                }}
              >
                {/* we really should make sure our icons are uniform in size / canvas area */}
                {!isDrawerOpen ? <Hamburger className="w-12" /> : <Close className="m-auto w-3" />}
              </button>
            )}
          </div>

          {/* Mobile Dropdown */}
          <NavContainer
            backgroundColor="bg-white dark:bg-secondary-900"
            position="right"
            isDrawerOpen={isDrawerOpen}
            drawerBreakpoint="lg"
            className="p-6 pb-16"
            onRequestClose={closeDrawer}
            headerRef={headerRef}
            showOverlay={false}
            fullWidth
          >
            <div style={{ minHeight: `calc(100vh - var(--nav-container-drawer-offset)` }} className="flex flex-col ">
              <StaggerList
                className={`flex-1 list-none pb-[var(--nav-container-drawer-offset)]`}
                isVisible={isDrawerOpen}
              >
                {mobileMainNavLinks.map((link) => {
                  if (!link) return null
                  if (isTypeComponentCtaLink(link)) {
                    return (
                      <StaggerListItem key={link.sys.id} className="not-last:border-b-[1px] border-secondary-200">
                        <DynamicAction
                          className={cx('block px-6 py-3 text-xl ')}
                          onClick={closeDrawer}
                          {...(link.fields as any)}
                          id={link.sys.id}
                          nonCtaLink
                        />
                      </StaggerListItem>
                    )
                  }
                  if (isTypeHeaderDropdownMenu(link)) {
                    return (
                      <StaggerListItem
                        key={link.sys.id}
                        className={cx(
                          `p-0 not-last:border-b-[1px] border-b-secondary-200 [&_button]:px-6 [&_button]:py-3 [&_button]:w-full [&_button]:rounded-none [&_button]:bg-secondary-50 [&_button]:items-center [&_button]:justify-between`
                        )}
                      >
                        <Disclosure
                          className="bg-transparent! dark:hover:bg-transparent"
                          heading={
                            <div className="flex items-center justify-between text-xl ">
                              <>{link.fields.title}</>
                            </div>
                          }
                          ArrowIcon={MobileDisclosureArrow}
                        >
                          <ul className="flex flex-col">
                            <>
                              {link?.fields?.links?.map((nestedLink) => (
                                <li className="list-none" key={nestedLink?.sys.id}>
                                  <DynamicAction
                                    className="block py-3 pl-10 text-lg  border-t-[1px] border-secondary-100"
                                    onClick={closeDrawer}
                                    {...(nestedLink?.fields as any)}
                                    id={link.sys.id}
                                    nonCtaLink
                                  />
                                </li>
                              ))}
                            </>
                          </ul>
                        </Disclosure>
                      </StaggerListItem>
                    )
                  }
                  if (isTypeHeaderRichMenu(link)) {
                    return (
                      <StaggerListItem
                        key={link.sys.id}
                        className={cx(
                          `p-0 not-last:border-b-[1px] border-b-secondary-200 [&_button]:px-6 [&_button]:py-3 [&_button]:w-full [&_button]:rounded-none [&_button]:bg-secondary-50 [&_button]:items-center [&_button]:justify-between`
                        )}
                      >
                        <Disclosure
                          className="bg-transparent! dark:hover:bg-transparent"
                          heading={<div className="flex items-center justify-between text-xl">{link.fields.title}</div>}
                          ArrowIcon={MobileDisclosureArrow}
                        >
                          <div className="px-2 pb-6">
                            <HeaderRichMenuContent
                              links={link.fields.links}
                              cards={link.fields.cards}
                              includeNewsletter={false}
                              isCondensed
                              onLinkClick={closeDrawer}
                            />
                          </div>
                        </Disclosure>
                      </StaggerListItem>
                    )
                  }
                  return null
                })}
                {showSearch && (
                  <StaggerListItem className="px-4 py-6 not-last:border-b-[1px] border-secondary-200">
                    <SearchNavIcon>
                      <span className="text-lg ml-2">Search</span>
                    </SearchNavIcon>
                  </StaggerListItem>
                )}
              </StaggerList>
              {ctaNavLinks?.length > 0 && !AreMainNavItemsEmpty && (
                <div className="sticky bottom-[var(--nav-container-drawer-offset)] translate-y-[62px] left-0 w-full bg-white dark:bg-secondary-900 flex flex-row flex-wrap items-center justify-between py-6">
                  {ctaNavLinks.map((ctaLink) => {
                    if (isTypeComponentCtaLink(ctaLink)) {
                      return (
                        <DynamicAction
                          key={ctaLink.sys.id}
                          onClick={closeDrawer}
                          {...(ctaLink.fields as any)}
                          size="Smaller"
                          className={cx('no-underline!')}
                        />
                      )
                    }
                    return null
                  })}
                </div>
              )}
            </div>
          </NavContainer>
        </nav>

        {/* Desktop Nav */}
        <motion.nav
          data-testid="bw-desktop-header"
          className={cx(
            'relative hidden max-w-8xl mx-auto px-6 xl:px-9 justify-between gap-4 lg:flex',
            !isStatic && 'xl:rounded-lg',
            !isStatic && bgColorClasses
          )}
          layout
          transition={transition}
          style={{
            boxShadow: useTransform(scrollY, (latest) => {
              if (isStatic) {
                return ''
              }
              if (latest < 400) {
                return ''
              }
              return 'none'
            }),
          }}
        >
          {/* Shadow / border layer */}
          {!isStatic && (
            <motion.div
              className={cx(
                'hidden xl:block absolute top-0 left-0 w-full h-full pointer-events-none z-10 rounded-lg shadow-even-around dark:shadow-navy-even-around-small',
                borderClasses
              )}
              transition={transition}
              style={{
                opacity: useTransform(scrollY, [0, 400], [1, 0]),
              }}
            />
          )}
          <motion.div
            className="flex gap-2 xl:gap-0 transition-transform duration-300 w-full"
            style={{
              height: useTransform(scrollY, [0, 400], ['88px', '68px']),
            }}
            transition={transition}
            layout
          >
            <div className="flex flex-1 items-center">
              <div className="w-36 2xl:w-44 py-3">
                <Link to={routes.index}>
                  <LogoHorizontal
                    className={cx(
                      'w-full',
                      backgroundColor === colors.primaryBlue || backgroundColor === colors.indigoBlue
                        ? 'text-white'
                        : 'text-primaryBlue',
                      'dark:text-white'
                    )}
                    title="Bitwarden homepage"
                  />
                </Link>
              </div>
              {mainNavLinks?.length > 0 && (
                <ul className="flex flex-1 gap-4 xl:gap-5 items-center justify-center list-none text-base 2xl:text-lg pl-6 pr-2">
                  {mainNavLinks.map((link) => {
                    const linkFields = { ...link.fields, style: buttonStyles.btnPlain }
                    if (isTypeComponentCtaLink(link)) {
                      return (
                        <li key={link.sys.id} className="leading-5">
                          <DynamicAction
                            className={cx(
                              'text-sm xl:text-base py-3 no-underline border-0 p-0 transition-colors duration-200',
                              backgroundColor === colors.primaryBlue &&
                                `text-white hover:text-secondary-200 dark:hover:text-primaryBlue`,
                              backgroundColor === colors.white &&
                                `hover:text-bw-deep-blue dark:text-white dark:hover:text-primaryBlue`
                            )}
                            {...linkFields}
                            nonCtaLink
                          />
                        </li>
                      )
                    }
                    if (isTypeHeaderDropdownMenu(link)) {
                      return (
                        <li key={link.sys.id} className="leading-5">
                          <HeaderDropdownMenu {...link.fields} headerBackgroundColor={backgroundColor} />
                        </li>
                      )
                    }
                    if (isTypeHeaderRichMenu(link)) {
                      return (
                        <li key={link.sys.id} className="leading-5">
                          <HeaderRichMenu
                            {...link.fields}
                            headerBackgroundColor={backgroundColor}
                            newsletterForm={newsletterForm}
                          />
                        </li>
                      )
                    }
                    return null
                  })}
                  {showSearch && (
                    <li>
                      <SearchNavIcon backgroundColor={backgroundColor} />
                    </li>
                  )}
                </ul>
              )}
            </div>
            <div className="flex gap-2 xl:gap-4 2xl:gap-5 items-center">
              {ctaNavLinks.map((ctaLink) => {
                if (isTypeComponentCtaLink(ctaLink)) {
                  const isBtnSolid = ctaLink.fields.style === buttonStyles.btnSolid
                  if (isBtnSolid) {
                    ctaBtnSolidCount += 1
                  }

                  const isAltColor = ctaBtnSolidCount % 2 === 0
                  const style = isAltColor ? buttonStyles.btnOutlined : ctaLink.fields.style
                  const uid = useId() + ctaLink.sys.id
                  const sharedProps = {
                    onClick: () => {
                      ;(ctaLink.fields as any).onClick?.()
                    },
                    to: ctaLink.fields.link?.fields.url,
                    id: uid,
                    'data-testid': 'cta-link',
                    plausibleEventName: ctaLink.fields.plausibleEventName,
                  }

                  return (
                    <Fragment key={uid}>
                      {/* Desktop */}
                      <CtaLink
                        {...sharedProps}
                        style={style}
                        className={cx(
                          'hidden! xl:inline-block!',
                          (style === buttonStyles.btnPlain || style === buttonStyles.btnText) &&
                            'no-underline! hover:underline! visited:underline! px-4 py-3'
                        )}
                      >
                        {ctaLink.fields.label}
                      </CtaLink>
                      {/* Mobile */}
                      <CtaLink
                        {...sharedProps}
                        style={buttonStyles.btnText}
                        className={cx('xl:hidden! no-underline! hover:underline! px-2 py-1 text-sm')}
                      >
                        {ctaLink.fields.label}
                      </CtaLink>
                    </Fragment>
                  )
                }
                return null
              })}
            </div>
          </motion.div>
        </motion.nav>
      </motion.header>
    </>
  )
}

type HeaderProps = HeaderComponentProps & {
  id: string
  nt_experiences: any
}

export default function Header(props: HeaderProps) {
  const Loader = useCallback(
    () => (
      <div className="bg-primaryBlue opacity-95 sticky top-0 left-0 z-50">
        <div className="animate-pulse bg-primaryBlue w-full h-[72px] lg:h-[64px] xl:h-[68px] relative hidden max-w-8xl mx-auto px-9 justify-between gap-4 lg:flex">
          <div className="flex items-center">
            <div className="w-36 2xl:w-44 py-3 mr-6 xl:mr-10 2xl:mr-12">
              <LogoHorizontal className="w-full text-white" />
            </div>
          </div>
        </div>
      </div>
    ),
    []
  )

  return (
    <Experience
      {...props}
      id={props.id}
      experiences={mapNtExperiences(props.nt_experiences)}
      component={HeaderComponent}
      loadingComponent={Loader}
      passthroughProps={{ headerBanners: props.headerBanners, newsletterForm: props.newsletterForm }}
    />
  )
}
